// 从 user-poster-editor/src/collapse/views/materialCollapse/components/search/tagMenu.tsx 搬迁
import React, { useCallback, useMemo } from 'react';
import { Dropdown, Menu, Tag } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import './index.less';
import { SubMenu } from 'rc-menu';
import { getLocaleStore } from '@/i18n';
import { IColorItem, TextCategoryItem } from '@/types';
import MaterialContentFilter from '../material-content-filter';

interface TagMenuProps {
    color: IColorItem | null;
    setColor: Function;
    tagsLimit?: number;
    list?: TextCategoryItem[];
    tagChecked?: number | TAG_TYPE;
    tagChange?: (item: TextCategoryItem) => void;
    sticky?: boolean;
    stickyTop?: number;
    colorsList: IColorItem[];
}

export enum TAG_TYPE {
    'all' = 'all',
    'recently' = 'recently',
    'tag' = 'tag',
    'more' = 'more',
    'moreItem' = 'moreItem',
}

const DEFAULT_TAG: () => TextCategoryItem[] = () => {
    const { $t } = getLocaleStore();

    return [
        {
            id: TAG_TYPE.all,
            name: $t((v) => v.collapse.materialCollapse.all),
            materials: [],
        },
        {
            id: TAG_TYPE.recently,
            name: $t((v) => v.collapse.materialCollapse.recentUsed),
            materials: [],
        },
    ];
};

const DEFAULT_TAG_MORE: () => TextCategoryItem = () => {
    const { $t } = getLocaleStore();

    return {
        id: TAG_TYPE.more,
        name: $t((v) => v.collapse.materialCollapse.more),
        materials: [],
    };
};
const DEFAULT_TAG_MORE_ITEM: () => TextCategoryItem = () => {
    const { $t } = getLocaleStore();

    return {
        id: TAG_TYPE.moreItem,
        name: $t((v) => v.collapse.materialCollapse.colors),
        materials: [],
        isShowIcon: true,
    };
};
const { CheckableTag } = Tag;

const TagMenu: React.FC<TagMenuProps> = ({
    tagsLimit = 5,
    tagChecked = DEFAULT_TAG()[0].id,
    list,
    tagChange,
    sticky = false,
    stickyTop = 0,
    color,
    setColor,
    colorsList,
}) => {
    const tagsViewList = useMemo(() => {
        return DEFAULT_TAG().concat(list ? list.slice(0, tagsLimit) : []);
    }, [list, tagsLimit]);

    const tagsMoreList = useMemo(() => {
        if (!list || list.length < tagsLimit) return [];
        return list.slice(tagsLimit).concat(DEFAULT_TAG_MORE_ITEM());
    }, [list, tagsLimit]);
    const MenuItems = (item: TextCategoryItem) => {
        return (
            <Menu.Item
                key={item.id}
                onClick={(e: any) => {
                    if (item.isShowIcon) return false;
                    onChange?.(item, true);
                }}
                className={item.id === tagChecked ? 'active' : ''}
            >
                <span>{item.name}</span>
                {item.isShowIcon ? <IconFont name="ChevronRightBlack" fontSize={12}></IconFont> : null}
            </Menu.Item>
        );
    };

    const menu = useMemo(() => {
        if (!tagsMoreList.length) return null;
        const backgroundColor = color?.color ? color.color : 'transparent';
        return (
            <Menu>
                {tagsMoreList?.map((item) =>
                    item.isShowIcon ? (
                        <SubMenu
                            key={item.id}
                            title={
                                <div className="ant-dropdown-menu-submenu-dom">
                                    {item.name}
                                    <div className="ant-dropdown-menu-submenu-dom__right">
                                        <div
                                            className="ant-dropdown-menu-submenu-dom__color"
                                            style={{
                                                backgroundColor: `${backgroundColor}`,
                                            }}
                                        ></div>
                                        <IconFont name="ChevronRightBlack" fontSize={12}></IconFont>
                                    </div>
                                </div>
                            }
                        >
                            <MaterialContentFilter
                                colorsList={colorsList}
                                handleClick={(isSetting: boolean) => {
                                    const data = isSetting ? item : DEFAULT_TAG()[0];
                                    onChange(data, true);
                                }}
                                {...{ color, setColor }}
                            />
                        </SubMenu>
                    ) : (
                        MenuItems(item)
                    ),
                )}
            </Menu>
        );
    }, [tagsMoreList, color, setColor]);

    const onChange = useCallback((item: TextCategoryItem, e: boolean) => {
        if (e) {
            tagChange?.(item);
        }
    }, []);

    const isMoreIndex = useMemo(() => {
        return tagsMoreList?.map((item) => item.id).findIndex((v) => v === tagChecked) ?? -1;
    }, [tagChecked, tagsMoreList]);

    const domRender = useMemo(() => {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                {tagsMoreList[isMoreIndex]?.name}
                {color ? (
                    <div
                        style={{
                            width: 6,
                            height: 6,
                            margin: '0 4px',
                            borderRadius: 4,
                            backgroundColor: `${color.color}`,
                        }}
                    ></div>
                ) : null}
            </div>
        );
    }, [isMoreIndex, tagsMoreList, color]);

    if (!list) return null;

    return (
        <div className="tag-menu-wrap" style={sticky ? { position: 'sticky', top: `${stickyTop}px` } : {}}>
            <ul className="tag-item-list">
                {tagsViewList.map((item) => (
                    <li className="tag-item" key={item.id}>
                        <CheckableTag
                            key={item.id}
                            checked={item.id === tagChecked}
                            onChange={(e) => onChange(item, e)}
                        >
                            {item.name}
                        </CheckableTag>
                    </li>
                ))}
                {menu ? (
                    <li className="tag-item" key={DEFAULT_TAG_MORE().id}>
                        <Dropdown
                            overlay={menu}
                            trigger={['click']}
                            autoFocus
                            onOpenChange={(e) => onChange(DEFAULT_TAG_MORE(), e)}
                        >
                            <CheckableTag
                                key={DEFAULT_TAG_MORE().id}
                                checked={isMoreIndex !== -1}
                                className="tag-item-inner"
                            >
                                {isMoreIndex !== -1 ? domRender : DEFAULT_TAG_MORE().name}
                                <IconFont name="ChevronRightBlack" fontSize={12}></IconFont>
                            </CheckableTag>
                        </Dropdown>
                    </li>
                ) : null}
            </ul>
        </div>
    );
};

export default React.memo(TagMenu);
